import { Middleware } from "@reduxjs/toolkit";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import {
  PATH_ADMIN_BUSINESS_METADATA_GET,
  PATH_ADMIN_BUSINESS_METADATA_PUT,
} from "../constants/urlPaths";
import { getIframeAuthHeaders } from "../utils/sessionTokenUtil";

export type BusinessId = string;

export type RewardProgramName = "DeltaSkybonus" | "Aadvantage" | "Unspecified";

export interface RawRewardProgram {
  rewardProgram: RewardProgramName;
  value: string;
}

export interface RawBusinessMetadata {
  businessId: BusinessId;
  rewardPrograms: RawRewardProgram[];
}

export type RewardProgramsMap = Partial<Record<RewardProgramName, string>>;

export interface BusinessMetadata {
  businessId: BusinessId;
  rewardPrograms: RewardProgramsMap;
}

const toRtkError = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    return {
      error: {
        status: error.response?.status,
        data: error.response?.data,
      },
    };
  }
  return { error: { status: "UNKNOWN_ERROR", data: undefined } };
};

export const businessMetadataApi = createApi({
  reducerPath: "adminMetadataApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["BusinessMetadata"],
  endpoints: (builder) => ({
    getBusinessMetadata: builder.query<BusinessMetadata, { businessId: BusinessId }>({
      queryFn: async ({ businessId }) => {
        try {
          const res = await axios.put<RawBusinessMetadata>(
            PATH_ADMIN_BUSINESS_METADATA_GET,
            { businessId },
            { headers: getIframeAuthHeaders() }
          );
          const raw = res.data;
          return {
            data: {
              businessId: raw.businessId,
              rewardPrograms: Object.fromEntries(
                raw.rewardPrograms.map((p) => [p.rewardProgram, p.value])
              ) as RewardProgramsMap,
            },
          };
        } catch (error: unknown) {
          return toRtkError(error);
        }
      },
      providesTags: ["BusinessMetadata"],
    }),
    updateBusinessMetadata: builder.mutation<BusinessId, BusinessMetadata>({
      queryFn: async (metadata) => {
        try {
          const rawPayload: RawBusinessMetadata = {
            businessId: metadata.businessId,
            rewardPrograms: (
              Object.entries(metadata.rewardPrograms) as [
                RewardProgramName,
                string | undefined
              ][]
            )
              .filter(
                (entry): entry is [RewardProgramName, string] =>
                  entry[1] != null && entry[1] !== ""
              )
              .map(([rewardProgram, value]) => ({ rewardProgram, value })),
          };
          const res = await axios.put<BusinessId>(
            PATH_ADMIN_BUSINESS_METADATA_PUT,
            rawPayload,
            { headers: getIframeAuthHeaders() }
          );
          return { data: res.data };
        } catch (error: unknown) {
          return toRtkError(error);
        }
      },
      invalidatesTags: ["BusinessMetadata"],
    }),
  }),
});

export const businessMetadataApiMiddleware: Middleware =
  businessMetadataApi.middleware;

export const {
  useGetBusinessMetadataQuery,
  useUpdateBusinessMetadataMutation,
} = businessMetadataApi;
